
import Vue from "vue";
import { Auth } from "aws-amplify";
import { prepareContactData, ContactData } from "@/lib/utils/contact";

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo() {
    if (process.env.NODE_ENV === "production") {
      return {};
    }
    return {
      meta: [
        {
          name: "robots",
          content: "none",
        },
      ],
    };
  },
  data() {
    return {
      userId: "",
      message: "",
      idPoolId: "",
      showFallback: false,
      mailTo: "nola.cs@indent.co.jp",
      mailSubject: "",
      mailBody: "",
      contactData: null as ContactData | null,
    };
  },
  computed: {
    diagInfo() {
      const BR = "\n";
      const text = `▼ご利用のブラウザ情報${BR}${window.navigator.userAgent}${BR}▼ご利用のデバイス情報${BR}${window.navigator.platform}${BR}▼ユーザーID${BR}${this.userId}${BR}▼調査用情報${BR}${this.idPoolId}${BR}`;
      return text;
    },
    mail(): string {
      if (!this.contactData) {
        return `mailto:${this.mailTo}`;
      }

      return `mailto:${this.mailTo}?subject=${encodeURIComponent(this.contactData.subject)}&body=${encodeURIComponent(
        this.contactData.body
      )}`;
    },
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
  },
  async created() {
    let userId = null;
    try {
      const user = await Auth.currentUserPoolUser();
      userId = user.signInUserSession.idToken.payload.sub;
    } catch (error) {
      // do nothing
    }
    this.userId = userId ?? "(not login)";

    const currentCredentials = (await Auth.currentCredentials()) as any;
    if (currentCredentials && currentCredentials.data) {
      this.idPoolId = currentCredentials.data.IdentityId;
    }

    // お問い合わせデータを事前生成
    const baseSubject = "Nolaに関する問い合わせ";
    const body = this.getEmailBody();
    this.contactData = prepareContactData(baseSubject, body, this.userId);
  },
  methods: {
    /**
     * メール本文を生成する共通メソッド
     */
    getEmailBody(): string {
      const BR = "\n";
      return `下記にお問い合わせ内容をご記入ください。${BR}▼内容${BR}（お問い合わせ/ご意見・ご要望）${BR}${BR}▼詳しい内容${BR}こちらに内容をご記入ください。${BR}※ スクリーンショットや画面録画があると、不具合の調査がスムーズになります。${BR}※ 原稿関連のお問い合わせについては、原稿情報としてWeb版にて該当のURLを教えてください。${BR}※ Fish関連のお問い合わせについては、マイページにある利用履歴から該当の履歴についてスクリーンショット等で教えてください。${BR}※ サブスクリプション関連のお問い合わせについては、決済メール（Google PlayやApp Storeの状態）やNola内の状態も合わせて教えてください。${BR}${BR}下記の項目は不具合の場合の調査に必要な情報です。${BR}${this.diagInfo}`;
    },

    /**
     * フォールバック情報を表示
     */
    showFallbackOptions() {
      if (!this.contactData) {
        return;
      }

      // 事前生成されたデータを使用
      this.mailSubject = this.contactData.subject;
      this.mailBody = this.contactData.body;

      // フォールバック表示
      this.showFallback = true;
      this.message = "";
    },

    /**
     * 指定された要素の内容をクリップボードにコピー
     */
    copyToClipboard(type?: string) {
      let el: HTMLElement;
      let successMessage = "";

      switch (type) {
        case "mailTo":
          el = this.$refs.mailToInput as HTMLElement;
          successMessage = "宛先をコピーしました。";
          break;
        case "subject":
          el = this.$refs.subjectInput as HTMLElement;
          successMessage = "件名をコピーしました。";
          break;
        case "body":
          el = this.$refs.bodyTextarea as HTMLElement;
          successMessage = "本文をコピーしました。";
          break;
        default:
          // 調査用情報のコピー（既存機能）
          el = this.$refs.textarea as HTMLElement;
          successMessage = "クリップボードにコピーしました。";
      }

      this.performCopy(el, successMessage);
    },

    /**
     * すべてのメール情報をコピー
     */
    copyAllMailInfo() {
      const allInfo = `宛先: ${this.mailTo}\n件名: ${this.mailSubject}\n\n本文:\n${this.mailBody}`;

      // 一時的なテキストエリアを作成してコピー
      const tempTextarea = document.createElement("textarea");
      tempTextarea.value = allInfo;
      document.body.appendChild(tempTextarea);

      this.performCopy(tempTextarea, "すべての情報をコピーしました。");

      document.body.removeChild(tempTextarea);
    },

    /**
     * 実際のコピー処理
     */
    performCopy(element: HTMLElement, successMessage: string) {
      try {
        const el = element as HTMLInputElement | HTMLTextAreaElement;

        if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
          // iOS対応
          const range = document.createRange();
          el.contentEditable = "true";
          el.readOnly = false;
          range.selectNodeContents(el);

          const s = window.getSelection();
          s!.removeAllRanges();
          s!.addRange(range);

          if (el.setSelectionRange) {
            el.setSelectionRange(0, 999999);
          }

          el.contentEditable = "false";
          el.readOnly = true;
        } else {
          el.select();
        }

        if (document.execCommand("copy")) {
          this.message = successMessage;
        } else {
          this.message = "コピーに失敗しました。手動でコピーしてください。";
        }
      } catch (error) {
        console.error("コピー処理でエラーが発生しました:", error);
        this.message = "コピーに失敗しました。手動でコピーしてください。";
      }
    },

    /**
     * フォールバック表示を隠す
     */
    hideFallback() {
      this.showFallback = false;
      this.message = "";
    },
  },
});
